import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Clock, Maximize2, Minimize2, ExternalLink, Plus, Minus } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon path issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: undefined,
  iconUrl: undefined,
  shadowUrl: undefined,
});

// Pulsing dot for live position
const pulsingIcon = new L.DivIcon({
  className: 'location-pulse-marker',
  html: `
    <div style="position:relative;width:20px;height:20px;">
      <div style="position:absolute;inset:-4px;background:#22c55e;border-radius:50%;opacity:0.25;animation:locping 2s ease-out infinite;"></div>
      <div style="position:absolute;inset:0;background:#22c55e;border-radius:50%;border:3px solid white;box-shadow:0 0 8px rgba(34,197,94,0.6);"></div>
    </div>
  `,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Prevent parent scroll while the user pans/pinches the map, AND expose zoom controls.
const MapEventBlocker = ({ onMap }) => {
  const map = useMap();

  useEffect(() => {
    onMap?.(map);
    const container = map.getContainer();
    const stopPropagation = (e) => e.stopPropagation();

    container.addEventListener('touchstart', stopPropagation, { passive: false });
    container.addEventListener('touchmove', stopPropagation, { passive: false });
    container.addEventListener('wheel', stopPropagation, { passive: false });

    return () => {
      container.removeEventListener('touchstart', stopPropagation);
      container.removeEventListener('touchmove', stopPropagation);
      container.removeEventListener('wheel', stopPropagation);
    };
  }, [map, onMap]);

  return null;
};

const LocationMap = ({ lat, lng, expiresAt, isOwn }) => {
  const [expired, setExpired] = useState(false);
  const [remaining, setRemaining] = useState('');
  const [expanded, setExpanded] = useState(false);
  const mapRef = useRef(null);

  useEffect(() => {
    const update = () => {
      if (!expiresAt) { setExpired(true); return; }
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) {
        setExpired(true);
        setRemaining('');
      } else {
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        setRemaining(`${mins}:${secs.toString().padStart(2, '0')}`);
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (expired) {
    return (
      <div className="rounded-xl p-3.5 flex items-center gap-3 bg-black/30 border border-white/5">
        <div className="w-9 h-9 rounded-full bg-gray-700/50 flex items-center justify-center flex-shrink-0">
          <MapPin className="w-4 h-4 text-gray-500" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-400">Localisation expirée</p>
          <p className="text-xs text-gray-600">Cette position n'est plus disponible</p>
        </div>
      </div>
    );
  }

  const mapHeight = expanded ? 360 : 220;
  const mapWidth = expanded ? 320 : 280;

  // Re-center on the live marker (useful after user panned away).
  const recenter = (e) => {
    e.stopPropagation();
    mapRef.current?.flyTo([lat, lng], 16, { duration: 0.6 });
  };
  const zoomIn = (e) => { e.stopPropagation(); mapRef.current?.zoomIn(); };
  const zoomOut = (e) => { e.stopPropagation(); mapRef.current?.zoomOut(); };
  const openInMaps = (e) => {
    e.stopPropagation();
    // iOS opens Apple Maps via maps://, Android resolves http geo: URL.
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const url = isIOS
      ? `maps://?ll=${lat},${lng}&q=${lat},${lng}`
      : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    window.open(url, '_blank');
  };

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{ width: `${mapWidth}px` }}
      data-testid="location-map-container"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Interactive Leaflet Map */}
      <div style={{ height: `${mapHeight}px`, width: '100%' }} className="relative transition-all duration-300">
        <MapContainer
          center={[lat, lng]}
          zoom={15}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          attributionControl={false}
          dragging={true}
          touchZoom={true}
          scrollWheelZoom={true}
          doubleClickZoom={true}
          boxZoom={false}
          tap={true}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={[lat, lng]} icon={pulsingIcon} />
          <MapEventBlocker onMap={(m) => { mapRef.current = m; }} />
        </MapContainer>

        {/* Top-right action stack: expand + open in Maps */}
        <div className="absolute top-2 right-2 z-[1000] flex flex-col gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="p-1.5 bg-black/65 hover:bg-black/85 rounded-lg backdrop-blur-sm transition-colors"
            data-testid="map-expand-btn"
            aria-label={expanded ? 'Réduire' : 'Agrandir'}
          >
            {expanded ? (
              <Minimize2 className="w-3.5 h-3.5 text-white" />
            ) : (
              <Maximize2 className="w-3.5 h-3.5 text-white" />
            )}
          </button>
          <button
            onClick={openInMaps}
            className="p-1.5 bg-black/65 hover:bg-black/85 rounded-lg backdrop-blur-sm transition-colors"
            data-testid="map-external-btn"
            aria-label="Ouvrir dans Maps"
          >
            <ExternalLink className="w-3.5 h-3.5 text-white" />
          </button>
        </div>

        {/* Bottom-right zoom & recenter controls */}
        <div className="absolute bottom-2 right-2 z-[1000] flex flex-col gap-1 rounded-lg overflow-hidden bg-black/65 backdrop-blur-sm">
          <button
            onClick={zoomIn}
            className="p-1.5 hover:bg-white/10 transition-colors"
            data-testid="map-zoom-in"
            aria-label="Zoom avant"
          >
            <Plus className="w-3.5 h-3.5 text-white" />
          </button>
          <button
            onClick={zoomOut}
            className="p-1.5 hover:bg-white/10 transition-colors border-t border-white/10"
            data-testid="map-zoom-out"
            aria-label="Zoom arrière"
          >
            <Minus className="w-3.5 h-3.5 text-white" />
          </button>
          <button
            onClick={recenter}
            className="p-1.5 hover:bg-white/10 transition-colors border-t border-white/10"
            data-testid="map-recenter"
            aria-label="Recentrer"
          >
            <MapPin className="w-3.5 h-3.5 text-green-400" />
          </button>
        </div>
      </div>

      {/* Info bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-black/40 backdrop-blur-sm">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs font-medium text-green-400">En direct</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-gray-400" />
          <span className="text-xs text-gray-400 font-mono">{remaining}</span>
        </div>
      </div>
    </div>
  );
};

export default LocationMap;
